import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import LoadingButton from '@mui/lab/LoadingButton';
import {
    alpha,
    Box,
    Button,
    Checkbox,
    DialogContent,
    Grid,
    IconButton,
    MenuItem,
    Select,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
} from '@mui/material';
import { LocalizationProvider, MobileDateTimePicker } from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import moment from 'moment';
import { useDialog } from 'muibox';
import { useSnackbar } from 'notistack';
import React, { useEffect, useMemo, useState } from 'react';
import { ScheduledTask } from '../../../../hooks/useMessageUsersScheduledSessions';
import DialogAdaptive from '../../../common/DialogAdaptive';
import StyledCardTitleBar from '../../../common/StyledCardTitleBar';
import StyledDialogActions from '../../../common/StyledDialogActions';

type Props = {
    scheduledMessages: ScheduledTask[];
    refetchScheduledMessages: () => void;
    bulkDistributeScheduledMessages: (
        scheduledTaskIds: string[],
        everyAmount: number,
        everyUnit: moment.unitOfTime.Base,
        startDate: Date,
    ) => Promise<{ success: boolean }>;
    bulkDistributeScheduledMessagesLoading: boolean;
    open: boolean;
    setOpen: (open: boolean) => void;
};

type SortField = 'name' | 'repostEvery' | 'nextRepostDate';
type SortDirection = 'asc' | 'desc';

// Sortable row component for drag and drop
const SortableRow = React.memo(
    ({
        scheduledMessage,
        selectedScheduledMessageIds,
        onCheckboxChange,
        reorderMode,
    }: {
        scheduledMessage: ScheduledTask;
        selectedScheduledMessageIds: string[];
        onCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
        reorderMode: boolean;
    }) => {
        const { attributes, listeners, setNodeRef, transform, transition, isDragging, setActivatorNodeRef } = useSortable({
            id: scheduledMessage._id!,
            disabled: !reorderMode,
        });

        const style = {
            transform: CSS.Transform.toString(transform),
            transition,
            opacity: isDragging ? 0.5 : 1,
        };

        return (
            <TableRow
                ref={setNodeRef}
                style={style}
                sx={{
                    '&:last-child td, &:last-child th': {
                        border: 0,
                    },
                    backgroundColor: theme =>
                        selectedScheduledMessageIds.includes(scheduledMessage._id!)
                            ? alpha(theme.palette.primary.main, 0.2)
                            : 'transparent',
                }}
            >
                <TableCell align="center">
                    {reorderMode ? (
                        <IconButton
                            ref={setActivatorNodeRef}
                            size="small"
                            {...attributes}
                            {...listeners}
                            sx={{
                                cursor: 'grab',
                                '&:active': { cursor: 'grabbing' },
                                touchAction: 'none', // Prevents scrolling when touching the drag handle
                            }}
                        >
                            <DragIndicatorIcon fontSize="small" />
                        </IconButton>
                    ) : (
                        <Checkbox
                            checked={selectedScheduledMessageIds.includes(scheduledMessage._id!)}
                            onChange={onCheckboxChange}
                            color="primary"
                            size="small"
                            sx={{
                                cursor: 'pointer',
                                '&:hover': {
                                    backgroundColor: 'transparent',
                                },
                            }}
                        />
                    )}
                </TableCell>
                <TableCell>
                    <Typography variant="body2" color="text.primary" noWrap>
                        {scheduledMessage.payload?.name || 'No Name'}
                    </Typography>
                </TableCell>
                <TableCell align="center">
                    {scheduledMessage.repeatEveryAmount && scheduledMessage.repeatEveryUnit && (
                        <Typography variant="body2" color="text.secondary" noWrap>
                            {scheduledMessage.repeatEveryAmount.toLocaleString()}{' '}
                            {scheduledMessage.repeatEveryUnit.charAt(0).toUpperCase() +
                                scheduledMessage.repeatEveryUnit.slice(1).toLowerCase()}
                        </Typography>
                    )}
                </TableCell>
                <TableCell>
                    <Typography variant="body2" color="text.secondary" noWrap>
                        {moment(scheduledMessage.scheduleDateTime).format('DD/MM/YYYY hh:mm A')}
                    </Typography>
                </TableCell>
            </TableRow>
        );
    },
);

const MessageUsersPriorityMessageBulkDistributeDialog = (props: Props) => {
    const {
        scheduledMessages,
        refetchScheduledMessages,
        bulkDistributeScheduledMessages,
        bulkDistributeScheduledMessagesLoading,
        open,
        setOpen,
    } = props;
    const { enqueueSnackbar } = useSnackbar();
    const dialog = useDialog();

    const [selectedScheduledTaskIds, setSelectedScheduledTaskIds] = useState<string[]>([]);
    const [selectedScheduledTasks, setSelectedScheduledTasks] = useState<ScheduledTask[]>([]);
    const [offsetAmount, setOffsetAmount] = useState<number>(0);
    const [offsetUnit, setOffsetUnit] = useState<moment.unitOfTime.Base>('hours');
    const [startDate, setStartDate] = useState<moment.Moment | null>(moment());

    // Reorder mode state
    const [reorderMode, setReorderMode] = useState<boolean>(false);
    const [orderedScheduledTasks, setOrderedScheduledTasks] = useState<ScheduledTask[]>([]);
    const [hasCustomOrder, setHasCustomOrder] = useState<boolean>(false);

    // Sorting state
    const [sortField, setSortField] = useState<SortField>('nextRepostDate');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

    // Initialize ordered messages when autoReposts changes
    useEffect(() => {
        if (orderedScheduledTasks.length === 0 || orderedScheduledTasks.length !== scheduledMessages.length) {
            // Initialize with sorted messages only if we don't have a custom order yet
            if (!hasCustomOrder) {
                setOrderedScheduledTasks(sortedScheduledMessages);
            } else {
                // Update ordered messages to include any new items while preserving order
                const existingIds = new Set(orderedScheduledTasks.map(m => m._id));
                const newAutoReposts = scheduledMessages.filter(m => !existingIds.has(m._id));
                setOrderedScheduledTasks([...orderedScheduledTasks, ...newAutoReposts]);
            }
        }
    }, [scheduledMessages]);

    // Sort the scheduled messages based on current sort settings
    const sortedScheduledMessages = useMemo(() => {
        return [...scheduledMessages].sort((a, b) => {
            let aValue: any;
            let bValue: any;

            switch (sortField) {
                case 'name':
                    aValue = a.payload?.name?.toLowerCase();
                    bValue = b.payload?.name?.toLowerCase();
                    break;
                case 'repostEvery':
                    // Convert to minutes for comparison
                    const aMinutes = moment
                        .duration(a.repeatEveryAmount, a.repeatEveryUnit as moment.unitOfTime.DurationConstructor)
                        .asMinutes();
                    const bMinutes = moment
                        .duration(b.repeatEveryAmount, b.repeatEveryUnit as moment.unitOfTime.DurationConstructor)
                        .asMinutes();
                    aValue = aMinutes;
                    bValue = bMinutes;
                    break;
                case 'nextRepostDate':
                    aValue = new Date(a.scheduleDateTime).getTime();
                    bValue = new Date(b.scheduleDateTime).getTime();
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) {
                return sortDirection === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortDirection === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [scheduledMessages, sortField, sortDirection]);

    // Use ordered messages if we have a custom order, otherwise use sorted messages
    const displayedScheduledMessages = hasCustomOrder ? orderedScheduledTasks : sortedScheduledMessages;

    const handleSort = (field: SortField) => {
        if (reorderMode) return; // Disable sorting in reorder mode

        // Apply sorting to the current ordered messages
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }

        // Reset to sorted view and clear custom order
        setHasCustomOrder(false);
    };

    const handleClose = () => {
        // Reset selected scheduled messages when the dialog is closed
        setSelectedScheduledTasks([]);
        setSelectedScheduledTaskIds([]);
        setStartDate(moment());
        setReorderMode(false);
        setOrderedScheduledTasks([]);
        setHasCustomOrder(false);
        setOpen(false);
    };

    // Handle delete action
    const handleDistribute = () => {
        if (!startDate) {
            return;
        }

        dialog
            .confirm({
                title: 'Distribute Priority Messages',
                message: `Are you sure you want to distribute ${selectedScheduledTasks.length.toLocaleString()} Priority Messages?`,
                ok: {
                    text: 'Distribute',
                    color: 'primary',
                    variant: 'contained',
                },
                cancel: {
                    text: 'Cancel',
                    color: 'secondary',
                    variant: 'text',
                },
            })
            .then(() => {
                // Use the order from displayedScheduledMessages to maintain custom order
                const orderedIds = displayedScheduledMessages.filter(ar => selectedScheduledTaskIds.includes(ar._id!)).map(ar => ar._id!);

                // Perform distribute action
                bulkDistributeScheduledMessages(orderedIds, offsetAmount, offsetUnit, startDate.toDate())
                    .then(res => {
                        if (res.success) {
                            enqueueSnackbar('Priority Messages distributed successfully', {
                                variant: 'success',
                            });

                            refetchScheduledMessages();
                            handleClose();
                        } else {
                            enqueueSnackbar('Failed to distribute Priority Messages', { variant: 'error' });
                        }
                    })
                    .catch(err => {
                        console.error(err);
                        enqueueSnackbar('Failed to distribute Priority Messages', { variant: 'error' });
                    });
            })
            .catch(() => {
                // Handle cancel
            });
    };

    // Drag and drop sensors with activation constraints for mobile
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8, // Require 8px movement before drag starts (allows scrolling)
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            setOrderedScheduledTasks(items => {
                const oldIndex = items.findIndex(item => item._id === active.id);
                const newIndex = items.findIndex(item => item._id === over.id);

                return arrayMove(items, oldIndex, newIndex);
            });
            // Mark that we now have a custom order
            setHasCustomOrder(true);
        }
    };

    const handleReorderModeToggle = () => {
        if (!reorderMode && !hasCustomOrder) {
            // Entering reorder mode for the first time - initialize with current sorted view
            setOrderedScheduledTasks([...sortedScheduledMessages]);
        }
        setReorderMode(!reorderMode);
    };

    const handleRandomizeOrder = () => {
        // Fisher-Yates shuffle algorithm
        const shuffleArray = (array: ScheduledTask[]) => {
            const newArray = [...array];
            for (let i = newArray.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
            }
            return newArray;
        };

        // Use current displayed scheduled messages as base for randomization
        const shuffled = shuffleArray(displayedScheduledMessages);
        setOrderedScheduledTasks(shuffled);
        setHasCustomOrder(true);

        // Optionally enable reorder mode if not already enabled
        if (!reorderMode) {
            setReorderMode(true);
        }
    };

    return (
        <DialogAdaptive open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
            <StyledCardTitleBar title={`Priority Messages Bulk Distribute`} />
            <DialogContent sx={{ pl: 0, pr: 0 }}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                    <Stack direction="column" spacing={2}>
                        <Typography variant="body1" color="text.secondary" pl={2} pr={2}>
                            Select the Priority Message you want to distribute their scheduled repost times. Each Priority Message will be
                            scheduled one after another at the selected interval. You can select all Priority Messages by clicking the
                            checkbox in the header.
                        </Typography>

                        <Box sx={{ pl: 2, pr: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                {selectedScheduledTasks.length.toLocaleString()} selected
                            </Typography>

                            <Stack direction="row" spacing={1} alignItems="center">
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<ShuffleIcon />}
                                    onClick={handleRandomizeOrder}
                                    disabled={displayedScheduledMessages.length === 0}
                                    sx={{
                                        textTransform: 'none',
                                        minWidth: 'auto',
                                    }}
                                >
                                    Randomize
                                </Button>

                                <Grid container alignItems="center" spacing={0.5} sx={{ width: 'auto' }}>
                                    <Grid item xs="auto">
                                        <Typography
                                            variant="body2"
                                            color={hasCustomOrder && !reorderMode ? 'secondary.main' : 'text.secondary'}
                                            fontWeight={hasCustomOrder && !reorderMode ? 'bold' : 'normal'}
                                            sx={{ flexShrink: 0 }}
                                        >
                                            Reorder
                                        </Typography>
                                    </Grid>
                                    <Grid item xs="auto">
                                        <Switch
                                            checked={reorderMode}
                                            onChange={handleReorderModeToggle}
                                            size="small"
                                            color="primary"
                                            sx={{ flexShrink: 1 }}
                                        />
                                    </Grid>
                                </Grid>
                            </Stack>
                        </Box>

                        {reorderMode && (
                            <Typography variant="body2" color="primary" pl={2} pr={2} sx={{ fontStyle: 'italic' }}>
                                Use the drag handle icon (☰) to reorder rows. The distribution will follow this custom order.
                            </Typography>
                        )}

                        {hasCustomOrder && !reorderMode && (
                            <Typography variant="body2" color="primary" pl={2} pr={2} sx={{ fontStyle: 'italic' }}>
                                Using custom order. Click any column header to reset to sorted view.
                            </Typography>
                        )}

                        <Box
                            sx={{
                                width: '100%',
                                overflow: 'hidden',
                                backgroundColor: theme => alpha(theme.palette.primary.main, 0.1),
                            }}
                        >
                            <TableContainer
                                sx={{
                                    maxHeight: 400,
                                    // Reduce the padding between the cells
                                    '& .MuiTableCell-root': {
                                        padding: '4px 8px',
                                    },
                                    // remove the left padding of the first cell
                                    '& .MuiTableCell-root:first-of-type': {
                                        paddingLeft: 0,
                                    },
                                    // remove the right padding of the last cell
                                    '& .MuiTableCell-root:last-of-type': {
                                        paddingRight: 2,
                                    },
                                }}
                            >
                                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                    <Table
                                        size="small"
                                        sx={{
                                            // Pin the header row
                                            '& thead th': {
                                                position: 'sticky',
                                                top: 0,
                                                backgroundColor: 'rgb(204, 223, 227)',
                                                zIndex: 1,
                                            },
                                            // Pin the first column
                                            '& th:first-of-type, & td:first-of-type': {
                                                position: 'sticky',
                                                left: 0,
                                                zIndex: 1,
                                            },
                                        }}
                                    >
                                        <TableHead
                                            sx={{
                                                backgroundColor: 'rgb(204, 223, 227)',
                                                // pin the first column
                                                '& th:first-of-type': {
                                                    position: 'sticky',
                                                    left: 0,
                                                    zIndex: 2,
                                                },
                                            }}
                                        >
                                            <TableRow
                                                sx={{
                                                    // every cell in the header row
                                                    '& th': {
                                                        backgroundColor: theme => theme.palette.background.paper,
                                                        color: theme => theme.palette.text.primary,
                                                        fontWeight: 'bold',
                                                    },
                                                }}
                                            >
                                                <TableCell width={50} align="center">
                                                    {!reorderMode && (
                                                        <Checkbox
                                                            indeterminate={
                                                                selectedScheduledTaskIds.length > 0 &&
                                                                selectedScheduledTaskIds.length < displayedScheduledMessages.length
                                                            }
                                                            checked={selectedScheduledTasks.length === displayedScheduledMessages.length}
                                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                                if (e.target.checked) {
                                                                    setSelectedScheduledTaskIds(
                                                                        displayedScheduledMessages.map(autoRepost => autoRepost._id!),
                                                                    );
                                                                    setSelectedScheduledTasks(displayedScheduledMessages);
                                                                } else {
                                                                    setSelectedScheduledTaskIds([]);
                                                                    setSelectedScheduledTasks([]);
                                                                }
                                                            }}
                                                            color="primary"
                                                            size="small"
                                                            sx={{
                                                                cursor: 'pointer',
                                                                '&:hover': {
                                                                    backgroundColor: 'transparent',
                                                                },
                                                            }}
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <TableSortLabel
                                                        active={sortField === 'name' && !reorderMode && !hasCustomOrder}
                                                        direction={sortField === 'name' ? sortDirection : 'asc'}
                                                        onClick={() => handleSort('name')}
                                                        disabled={reorderMode}
                                                        sx={{
                                                            fontWeight: 'bold',
                                                            '& .MuiTableSortLabel-icon': {
                                                                color: 'inherit !important',
                                                            },
                                                            cursor: reorderMode ? 'default' : 'pointer',
                                                        }}
                                                    >
                                                        Name
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell align="center" width={110}>
                                                    <TableSortLabel
                                                        active={sortField === 'repostEvery' && !reorderMode && !hasCustomOrder}
                                                        direction={sortField === 'repostEvery' ? sortDirection : 'asc'}
                                                        onClick={() => handleSort('repostEvery')}
                                                        disabled={reorderMode}
                                                        sx={{
                                                            fontWeight: 'bold',
                                                            '& .MuiTableSortLabel-icon': {
                                                                color: 'inherit !important',
                                                            },
                                                            cursor: reorderMode ? 'default' : 'pointer',
                                                        }}
                                                    >
                                                        Repeats Every
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell width={150}>
                                                    <TableSortLabel
                                                        active={sortField === 'nextRepostDate' && !reorderMode && !hasCustomOrder}
                                                        direction={sortField === 'nextRepostDate' ? sortDirection : 'asc'}
                                                        onClick={() => handleSort('nextRepostDate')}
                                                        disabled={reorderMode}
                                                        sx={{
                                                            fontWeight: 'bold',
                                                            '& .MuiTableSortLabel-icon': {
                                                                color: 'inherit !important',
                                                            },
                                                            cursor: reorderMode ? 'default' : 'pointer',
                                                        }}
                                                    >
                                                        Scheduled Date
                                                    </TableSortLabel>
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            <SortableContext
                                                items={displayedScheduledMessages.map(m => m._id!)}
                                                strategy={verticalListSortingStrategy}
                                            >
                                                {displayedScheduledMessages.map(scheduledMessage => (
                                                    <SortableRow
                                                        key={scheduledMessage._id}
                                                        scheduledMessage={scheduledMessage}
                                                        selectedScheduledMessageIds={selectedScheduledTaskIds}
                                                        reorderMode={reorderMode}
                                                        onCheckboxChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                            if (e.target.checked) {
                                                                setSelectedScheduledTaskIds([
                                                                    ...selectedScheduledTaskIds,
                                                                    scheduledMessage._id!,
                                                                ]);
                                                                setSelectedScheduledTasks([...selectedScheduledTasks, scheduledMessage]);
                                                            } else {
                                                                setSelectedScheduledTaskIds(
                                                                    selectedScheduledTaskIds.filter(id => id !== scheduledMessage._id),
                                                                );
                                                                setSelectedScheduledTasks(
                                                                    selectedScheduledTasks.filter(ar => ar._id !== scheduledMessage._id),
                                                                );
                                                            }
                                                        }}
                                                    />
                                                ))}
                                            </SortableContext>
                                        </TableBody>
                                    </Table>
                                </DndContext>
                            </TableContainer>
                        </Box>

                        <Box sx={{ pl: 2, pr: 2 }}>
                            <Stack spacing={2}>
                                <Typography variant="body1" color="text.secondary">
                                    Select the start date/time and the interval to start scheduling Priority Messages from. All the selected
                                    Priority Messages will be scheduled this much apart from each other.
                                </Typography>

                                {/* Start Date/Time Input */}
                                <Stack direction="row" spacing={1} alignItems="center">
                                    {/* Amount Input */}
                                    <TextField
                                        label="Every..."
                                        type="number"
                                        value={offsetAmount}
                                        onChange={e => setOffsetAmount(Math.max(1, parseInt(e.target.value, 10) || 0))}
                                        fullWidth
                                        InputProps={{
                                            inputProps: { min: 1 },
                                        }}
                                        error={offsetAmount < 1}
                                    />

                                    {/* Unit Select */}
                                    <Select
                                        value={offsetUnit}
                                        size="small"
                                        onChange={e => setOffsetUnit(e.target.value as moment.unitOfTime.Base)}
                                        fullWidth
                                    >
                                        <MenuItem value="minutes">Minutes</MenuItem>
                                        <MenuItem value="hours">Hours</MenuItem>
                                        <MenuItem value="days">Days</MenuItem>
                                        <MenuItem value="weeks">Weeks</MenuItem>
                                        <MenuItem value="months">Months</MenuItem>
                                    </Select>
                                </Stack>

                                <MobileDateTimePicker
                                    label="Starting from..."
                                    value={startDate}
                                    onChange={newValue => setStartDate(newValue)}
                                />
                            </Stack>
                        </Box>
                    </Stack>
                </LocalizationProvider>
            </DialogContent>

            <StyledDialogActions>
                <Button onClick={handleClose} color="secondary" variant="text">
                    Cancel
                </Button>
                <LoadingButton
                    variant="contained"
                    color="primary"
                    size="small"
                    loading={bulkDistributeScheduledMessagesLoading}
                    disabled={selectedScheduledTaskIds.length === 0 || offsetAmount <= 0 || !startDate}
                    onClick={() => {
                        handleDistribute();
                    }}
                >
                    <span>Distribute Selected</span>
                </LoadingButton>
            </StyledDialogActions>
        </DialogAdaptive>
    );
};

export default MessageUsersPriorityMessageBulkDistributeDialog;
